import { Link } from "react-router-dom";
import { apiBaseUrl } from "../lib/cmsApi";
import { resolveCmsImage } from "../lib/cmsAssets";
import homeFallbackContent from "../lib/fallbackContent/home";
import { mergeCmsSection } from "../lib/mergeCmsSection";
import joinHero from "../assets/home-join/join-movement-hero.svg";

/**
 * Join the Movement — cream panel, yellow top bar cut by oversized circular
 * photo on the right (matches design reference).
 * CMS section key: `join_movement`
 *
 * Breakpoints:
 * - <768 (mobile): stacked text → image, buttons full-width column
 * - 768–1279 (tablet): two-column, scaled type/image
 * - 1280+ (desktop): 1440 reference proportions
 */
export default function JoinMovement({ data }) {
  const section = mergeCmsSection(data, homeFallbackContent.join_movement);
  const paragraphs = Array.isArray(section.paragraphs) ? section.paragraphs : [];
  const primaryLink = section.primaryCtaLink || "/contact-us";
  const secondaryLink = section.secondaryCtaLink || "/contact-us";
  const primaryText = section.primaryCtaText || "Partner With ASSET";
  const secondaryText = section.secondaryCtaText || "Become a Member";

  const imageSrc =
    section.image ||
    resolveCmsImage(section.imageKey, apiBaseUrl) ||
    joinHero;

  const imageAlt = section.imageAlt || "ASSET community team";

  return (
    <section
      id="join-movement"
      className="relative z-[2] overflow-hidden bg-[#FDFCF8]"
      aria-label={section.title || "Be Part of India's Engineering Future"}
    >
      {/* ── Mobile (<768px): single column ── */}
      <div className="flex flex-col md:hidden">
        <div className="flex h-14 w-full items-center bg-[#F5C542] px-6 sm:h-16 sm:px-8">
          <p className="font-Plus Jakarta Sans text-[15px] font-bold text-[#0f172a] sm:text-[17px]">
            {section.eyebrow || "Join the Movement"}
          </p>
        </div>

        <div className="px-6 py-8 sm:px-8 sm:py-10">
          <h2 className="max-w-[500px] text-[1.5rem] font-bold leading-[1.2] tracking-tight text-[#0f172a] sm:text-[1.75rem]">
            {section.title || "Be Part of India's Engineering Future"}
          </h2>

          <div className="mt-5 max-w-[480px] space-y-3 text-[0.8rem] leading-relaxed text-[#2B2B2B] sm:mt-6 sm:space-y-4 sm:text-[0.875rem]">
            {paragraphs.map((paragraph, index) => (
              <p key={`jm-p-m-${index}`}>{paragraph}</p>
            ))}
          </div>

          <div className="mt-6 flex w-full flex-col gap-3 sm:mt-8">
            <Link
              to={primaryLink}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0082DC] px-8 py-3 text-sm font-Inter font-semibold text-white transition hover:bg-[#2563EB]"
            >
              {primaryText}
              <span aria-hidden>→</span>
            </Link>
            <Link
              to={secondaryLink}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-black px-8 py-3 text-sm font-semibold text-white transition hover:bg-neutral-900"
            >
              {secondaryText}
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>

        <div className="flex justify-center px-6 pb-8 pt-2 sm:px-8 sm:pb-10">
          <div className="aspect-square w-full max-w-[min(100%,340px)] overflow-hidden rounded-full border-[3px] border-b-[1px] border-white shadow-[0_12px_40px_rgba(15,23,42,0.14)] sm:max-w-[400px]">
            <img
              src={imageSrc}
              alt={imageAlt}
              loading="lazy"
              className="h-full w-full scale-y-[1.02] object-cover object-[center_28%] origin-top"
            />
          </div>
        </div>
      </div>

      {/* ── Tablet + desktop (≥768px): two-column ── */}
      <div className="relative hidden h-[560px] md:block lg:h-[640px] xl:h-[700px]">
        {/* Yellow banner */}
        <div className="absolute left-0 top-0 z-[1] flex h-16 w-full items-center bg-[#F5C542] pl-10 lg:h-20 lg:w-[80%] lg:pl-12 xl:h-24 xl:w-[85%] xl:pl-16 2xl:w-[82%]">
          <p className="font-Plus Jakarta Sans text-[1.25rem] font-bold leading-none text-[#19224C] lg:text-[1.75rem] xl:text-[2.5rem] 2xl:text-[3rem]">
            {section.eyebrow || "Join the Movement"}
          </p>
        </div>

        {/* Circular image — ~70–85% on tablet, full on desktop */}
        <div
          className="absolute top-1/2 right-0 z-[2] isolate h-[420px] w-[420px] -translate-y-1/2 translate-x-[30%] overflow-hidden rounded-full border-[3px] border-b-[1px] border-white bg-white shadow-[0_16px_48px_rgba(15,23,42,0.12)] lg:h-[520px] lg:w-[520px] lg:translate-x-[26%] xl:h-[720px] xl:w-[720px] xl:translate-x-[22%] 2xl:h-[760px] 2xl:w-[760px] 2xl:translate-x-[20%]"
          aria-hidden
        >
          <img
            src={imageSrc}
            alt=""
            loading="lazy"
            className="h-full w-full origin-top scale-y-[1.02] bg-white object-cover object-[center_28%]"
          />
        </div>

        {/* Left copy — ~60% text column */}
        <div className="relative z-[3] flex h-full w-[60%] flex-col justify-center px-10 pb-10 pt-20 lg:px-12 lg:pt-24 xl:px-16 xl:pb-24 xl:pt-28">
          <div className="w-full max-w-[36rem]">
            <h2 className="max-w-[18rem] text-[2.5rem] font-bold leading-[1.15] tracking-tight text-[#000000] font-Plus Jakarta Sans whitespace-normal lg:max-w-[22rem] lg:text-[2.75rem] xl:max-w-none xl:text-[2.5rem] xl:whitespace-nowrap">
              {section.title || "Be Part of India's Engineering Future"}
            </h2>

            <div className="mt-5 max-w-[28rem] space-y-3 text-[0.9rem] leading-[1.7] text-[#2B2B2B] lg:mt-6 lg:space-y-4 lg:text-[0.95rem] xl:mt-8 xl:max-w-[26rem] xl:space-y-5 xl:text-base">
              {paragraphs.map((paragraph, index) => (
                <p key={`jm-p-d-${index}`}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-8 flex w-full flex-row items-stretch gap-3 lg:mt-10 lg:gap-4 xl:mt-12">
              <Link
                to={primaryLink}
                className="inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-lg bg-[#3B82F6] px-6 py-3 text-[14px] font-semibold text-white transition hover:bg-[#2563EB] xl:flex-none xl:px-8 xl:py-4 xl:text-[15px]"
              >
                {primaryText}
                <span aria-hidden>→</span>
              </Link>
              <Link
                to={secondaryLink}
                className="inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-lg bg-black px-6 py-3 text-[14px] font-semibold text-white transition hover:bg-neutral-900 xl:flex-none xl:px-8 xl:py-4 xl:text-[15px]"
              >
                {secondaryText}
                <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
